import { useEffect, useState, useMemo, useRef } from "react";
import styles from "./styles.module.scss";
import { ChatMessages } from "components/chat/chat-messages";
import { ChatSidebar } from "components/chat/chat-sidebar";
import { PickUpWhereLeftOffModal } from "components/chat/pick-up-modal";
import { useTourContext } from "components/product-tour";
import { useConversationSessions, useFetchAssessmentSessions, useHiringAssistant } from "hooks";
import { useSearchParams, useLocation } from "react-router-dom";
import { useAppSelector } from "state/hooks";
import { peekPendingRole, consumePendingRole, clearPendingRole } from "utils/pendingHiringIntent";
import { isTalentAudit, setAssistantFlow } from "helpers/assistantFlow";
import { clearCustomQuestionDraft } from "helpers/customQuestionDraft";
import { WELCOME_OPTIONS } from "components/chat/recruitment-cards/welcome-options";
import type { InputHints } from "helpers/assistantInputHints";
import isEqual from 'lodash/isEqual';
import type { Conversation, ConversationOption, Message, SalaryDetails } from "components/chat/chat-messages/types";

/** Composer configuration derived from the assistant API's `ui_hints`. */
export type { InputHints } from "helpers/assistantInputHints";

// Chat shapes are declared once in the chat's own types module; re-exported here
// because much of the app imports them from `features`.
export type {
  Message,
  MessageContent,
  Conversation,
  ConversationOption,
  ConversationData,
  SalaryDetails,
} from "components/chat/chat-messages/types";

// Get initial welcome options. Defined once in WELCOME_OPTIONS so the cards, the
// transcript labels and the isEqual layout check can never drift apart.
export const getInitialOptions = (): ConversationOption[] => WELCOME_OPTIONS;

const PublishAssessmentUI = () => {
  const user = useAppSelector((state) => state.user);
  const {
    firstName,
  } = user;
  const [currentOptions, setCurrentOptions] = useState<ConversationOption[] | null>(getInitialOptions());
  const isUsingDefault = isEqual(currentOptions, getInitialOptions());

  const DEFAULT_CONVERSATION: Conversation = {
    id: "welcome",
    title: "New Assessment",
    messages: [
      {
        id: "welcome-msg",
        content: `Hi ${firstName} 👋\nWelcome to Precisionmatch.\n I'm your Hiring Assistant — here to help you get your first hire right, without all the stress and guesswork.\nWhether you're starting from scratch or need help making sense of applicants you already have, I've got you covered.\nLet's start by understanding what you need help with today 👇`,
        sender: "system",
        timestamp: new Date(),
        isProcessed: isUsingDefault,
      },
    ],
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // Changed to use useConversationSessions hook
  const {
    conversationSessions,
    isLoading: loadingConversationSessions,
    error: conversationSessionError,
    fetchConversationSessions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversationSessions();
  const [selectedConversationId, setSelectedConversationId] = useState<string>("welcome");
  const [messages, setMessages] = useState<Message[]>(DEFAULT_CONVERSATION.messages);

  // Move all chat state to parent
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showInput, setShowInput] = useState<boolean>(false);
  const [inputHints, setInputHints] = useState<InputHints | null>(null);
  const [conversationState, setConversationState] = useState<string>("welcome");
  const [showOption, setShowOption] = useState<boolean>(true);
  const [formData, setFormData] = useState<Record<string, string | string[]>>({});
  const [salaryDetails, setSalaryDetails] = useState<SalaryDetails>({
    currency: 'USD',
    frequency: 'monthly',
    minSalary: '',
    maxSalary: '',
  });

  // Conversation tracking
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [assistantId, setAssistantId] = useState<string | null>(null);
  const [expectations, setExpectations] = useState<any>(null);
  const [assessmentStarted, setAssessmentStarted] = useState<boolean>(false);
  const [lastProcessedResponse, setLastProcessedResponse] = useState<any>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const { response } = useHiringAssistant(assessmentId);

  // "Pick Up Where You Left Off" — when a visitor typed a role on the landing
  // page, we resurface it here once onboarding AND the product tour are done,
  // then (on Continue) auto-pick the "hire for a role" flow and prefill the role.
  const { isActive: tourActive, hasFinished: tourFinished } = useTourContext();
  const onboardingComplete = user.completedProfile && user.completedBusiness;
  const [showPickup, setShowPickup] = useState(false);
  const [pickupRole, setPickupRole] = useState<string | null>(null);
  const [autoSelectScratch, setAutoSelectScratch] = useState(false);
  const [prefillRole, setPrefillRole] = useState<string | null>(null);
  const pickupResolvedRef = useRef(false);
  // Guards the URL-sync effect while a reset to the welcome screen is in flight.
  const pendingResetRef = useRef(false);

  useEffect(() => {
    if (pickupResolvedRef.current) return;
    // Only on a fresh welcome screen — never over an existing conversation.
    const onWelcome =
      !searchParams.get('id') &&
      !searchParams.get('id2') &&
      selectedConversationId === 'welcome' &&
      conversationState === 'welcome';
    if (!onWelcome) return;
    // Never stack on top of the onboarding modal. A returning user who logs in
    // with an incomplete profile lands here on `?onboarding=true&incomplete=…`
    // with the tour already marked seen, so the tour gate alone doesn't hold it
    // back the way it does on the sign-up route.
    if (!onboardingComplete) return;
    // Only after the tour has been completed/skipped for this account.
    if (!tourFinished || tourActive) return;
    const pending = peekPendingRole();
    if (pending) {
      setPickupRole(pending);
      setShowPickup(true);
    }
  }, [
    tourFinished,
    tourActive,
    onboardingComplete,
    searchParams,
    selectedConversationId,
    conversationState,
  ]);

  const handlePickupContinue = () => {
    pickupResolvedRef.current = true;
    const role = consumePendingRole();
    setShowPickup(false);
    if (!role) return;
    // The role is sent under the "hire for a role" flow (flow_2). Set it before
    // the option fires so the assistant treats the follow-up role input correctly.
    setAssistantFlow('flow_2');
    setPrefillRole(role);
    setAutoSelectScratch(true);
  };

  const handlePickupStartNew = () => {
    pickupResolvedRef.current = true;
    clearPendingRole();
    setShowPickup(false);
  };

  const clearParamsButKeepOnboarding = () => {
    const newParams = new URLSearchParams();

    const onboarding = searchParams.get("incomplete");
    if (onboarding) {
      newParams.set("incomplete", onboarding);
    }

    setSearchParams(newParams, { replace: true });
  };

  // Reset all state to initial values
  const resetToInitialState = (clearParams: boolean = false) => {
    // Set synchronously: clearing the ids is a state update, so for one render
    // the URL-sync effect would still see the old id against an empty URL and
    // write it back — leaving "Start a new conversation" stuck on the old one.
    pendingResetRef.current = true;
    setIsLoading(false);
    setShowInput(false);
    setInputHints(null);
    setConversationState("welcome");
    setCurrentOptions(getInitialOptions());
    setShowOption(true);
    setFormData({});
    setSalaryDetails({
      currency: 'USD',
      frequency: 'Monthly',
      minSalary: '',
      maxSalary: '',
    });
    setAssessmentId(null); // Clear assessment ID
    setAssistantId(null); // Clear assistant ID
    setExpectations(null);
    setAssessmentStarted(false);
    setLastProcessedResponse(null);
    setIsInitialized(true); // Mark as initialized to prevent history fetch
    setMessages([...DEFAULT_CONVERSATION.messages]);
    setSelectedConversationId("welcome");
    // Only clear params when explicitly requested to avoid infinite loops
    if (clearParams) {
      clearParamsButKeepOnboarding();
    }
  };

  useEffect(() => {
    const assessmentIdParam = searchParams.get('id');
    const assistantIdParam = searchParams.get('id2') || (location.state as any)?.assessment;
    const activeId = assessmentIdParam || assistantIdParam;

    // The URL sync effect below writes the id as soon as a new conversation is
    // created. That fires this effect too — but it's our own write for the
    // conversation already on screen, not a switch to a different one. Reloading
    // history here would wipe the live transcript, so only adopt the selection.
    const isSelfSync =
      !!activeId &&
      (activeId === String(assessmentId ?? '') || activeId === String(assistantId ?? ''));
    if (isSelfSync) {
      setSelectedConversationId(activeId);
      return;
    }

    if (activeId) {
      // Starting with an existing assessment - reset everything except the assessment ID
      setIsLoading(false);
      setShowInput(false);
      setInputHints(null);
      setConversationState("loading"); // Use loading state while fetching history
      setCurrentOptions(null); // Don't set initial options - let API response handle this
      setShowOption(false);
      setFormData({});
      setSalaryDetails({
        currency: 'USD',
        frequency: 'Monthly',
        minSalary: '',
        maxSalary: '',
      });
      setExpectations(null);
      setLastProcessedResponse(null);
      setSelectedConversationId(activeId);
      // Switching to a different conversation abandons an unsent carried-over
      // role — it must not land in this one's role step.
      setPrefillRole(null);
      // Set the correct IDs and flow type based on URL params
      if (assistantIdParam && !assessmentIdParam) {
        // Flow 1: id2 is the assistant ID
        setAssistantId(assistantIdParam);
        setAssessmentId(null); // Assessment ID will be set later when transitioning to flow2
        setAssistantFlow('flow_1');
      } else {
        // Flow 2: id is the assessment ID
        setAssessmentId(assessmentIdParam);
        setAssistantId(null);
        // A talent audit is also keyed by assessment_id, so ?id= alone can't tell
        // the two apart. Keep the audit flow if it's already set; conversation
        // history is authoritative and corrects this once it loads.
        if (!isTalentAudit()) {
          setAssistantFlow('flow_2');
        }
      }
      setAssessmentStarted(true); // If we have an assessment ID, it's already started
      setIsInitialized(false); // Let ChatMessages component load the history
      setMessages([]); // Clear messages - they'll be loaded from history
    } else {
      resetToInitialState();
    }
  }, [searchParams, location]);

  // Put the conversation id in the URL the moment the backend mints one, for all
  // three flows. Previously nothing did this at conversation start — flow_1 only
  // got ?id2= at the experience-level step, flow_2 only got ?id= at the basic
  // expectations form, and the talent audit never got one at all. Until then the
  // URL said "new conversation" while the chat was well underway, no sidebar row
  // could match, and a refresh lost the conversation.
  //
  // Goes through setSearchParams (not history.pushState, which react-router
  // doesn't observe) so `selectedConversationId` and the sidebar highlight update
  // in the same commit. `replace` keeps the back button pointing at the app, not
  // at a half-started conversation.
  useEffect(() => {
    // No conversation yet (welcome screen, or a reset just landed) — nothing to
    // sync, and this is where the reset guard is released.
    if (!assessmentId && !assistantId) {
      pendingResetRef.current = false;
      return;
    }
    if (pendingResetRef.current) return;

    if (assistantId && !searchParams.get('id2') && !searchParams.get('id')) {
      const next = new URLSearchParams(searchParams);
      next.set('id2', String(assistantId));
      setSelectedConversationId(String(assistantId));
      setSearchParams(next, { replace: true });
      return;
    }
    if (assessmentId && !searchParams.get('id') && !searchParams.get('id2')) {
      const next = new URLSearchParams(searchParams);
      next.set('id', String(assessmentId));
      setSelectedConversationId(String(assessmentId));
      setSearchParams(next, { replace: true });
    }
  }, [assessmentId, assistantId, searchParams, setSearchParams]);

  // Handle API response for new conversations
  useEffect(() => {
    if (response && response.conversation_data) {
      // Create new conversation from API response
      const convId = response.conversation_id?.toString() ?? `conv_${Date.now()}`;

      const systemMessage: Message = {
        id: `${convId}_1`,
        content: response.conversation_data.message,
        sender: "system",
        timestamp: new Date(),
      };
      setSelectedConversationId(convId);
      setMessages([systemMessage]);

      // Update URL if we have an assessment_id
      if (response.assessment_id) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('id', response.assessment_id);
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [response, searchParams, setSearchParams]);

  const handleNewConversation = () => {
    // Clear hiring assistant related localStorage items
    localStorage.removeItem("hiringAssistantFlowType");
    localStorage.removeItem("assessment_response_data");
    localStorage.removeItem("assessment_response_id");
    localStorage.removeItem("candidate_email");
    localStorage.removeItem("secondsLeft");
    // An unfinished custom-question form belongs to the conversation being left.
    clearCustomQuestionDraft();

    // Clear any typedText entries (typewriter cache)
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("typedText-")) {
        localStorage.removeItem(key);
      }
    });

    // Drop the conversation ids in the same commit as the params. The params
    // effect resets everything else, but it runs a render later — long enough
    // for the URL-sync effect to see a stale id and re-add it to the URL.
    pendingResetRef.current = true;
    setAssessmentId(null);
    setAssistantId(null);
    setSelectedConversationId('welcome');
    // An unsent carried-over role belongs to the conversation being abandoned —
    // don't let it resurface at the role step of the next one.
    setPrefillRole(null);
    // Clear URL params - this triggers the useEffect which will call resetToInitialState
    setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    fetchConversationSessions(); // Changed from fetchAssessmentSessions
  }, []);

  // Get current assessment ID for key - include both id and id2 params
  const currentAssessmentId = searchParams.get('id') || searchParams.get('id2') || (location.state as any)?.assessment || 'welcome';

  // Guard against stale messages flashing on conversation switch: URL params update
  // immediately but selectedConversationId (state) lags one render behind. During that
  // window the new ChatMessages instance would receive old messages containing components
  // (e.g. criteria) from the previous conversation. Pass [] until state catches up.
  const displayMessages = useMemo(() => {
    if (currentAssessmentId !== 'welcome' && currentAssessmentId !== selectedConversationId) {
      return [];
    }
    return messages;
  }, [currentAssessmentId, selectedConversationId, messages]);

  return (
    <div className={styles.assessmentContainer}>
      <PickUpWhereLeftOffModal
        show={showPickup}
        role={pickupRole}
        onContinue={handlePickupContinue}
        onStartNew={handlePickupStartNew}
      />
      <div className={styles.topnav} >
        <ChatSidebar
          onNewConversation={handleNewConversation}
          selectedItemId={selectedConversationId}
          className={styles.sidebar}
          loadingAssessmentSessions={loadingConversationSessions}
          assessmentSessionError={conversationSessionError}
          assessmentSessions={conversationSessions}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>

      <section className={styles.chatLayout}>
        <div className={styles.chatSidebar}>
          <ChatSidebar
            onNewConversation={handleNewConversation}
            selectedItemId={selectedConversationId}
            className={styles.sidebar}
            loadingAssessmentSessions={loadingConversationSessions}
            assessmentSessionError={conversationSessionError}
            assessmentSessions={conversationSessions}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>
        <div className={styles.chatContent}>
          <ChatMessages
            key={currentAssessmentId} // Force re-mount when assessment changes
            initialMessages={DEFAULT_CONVERSATION.messages}
            messages={displayMessages}
            setMessages={setMessages}
            systemName="Hiring Assistant"
            placeholder="Message Precision"
            className={styles.chatSection}
            fetchAssessmentSessions={fetchConversationSessions} // Changed from fetchAssessmentSessions
            // Pass all state as props
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            showInput={showInput}
            setShowInput={setShowInput}
            inputHints={inputHints}
            setInputHints={setInputHints}
            conversationState={conversationState}
            setConversationState={setConversationState}
            currentOptions={currentOptions}
            setCurrentOptions={setCurrentOptions}
            showOption={showOption}
            setShowOption={setShowOption}
            formData={formData}
            setFormData={setFormData}
            salaryDetails={salaryDetails}
            setSalaryDetails={setSalaryDetails}
            assessmentId={assessmentId}
            setAssessmentId={setAssessmentId}
            assistantId={assistantId}
            setAssistantId={setAssistantId}
            expectations={expectations}
            setExpectations={setExpectations}
            assessmentStarted={assessmentStarted}
            setAssessmentStarted={setAssessmentStarted}
            lastProcessedResponse={lastProcessedResponse}
            setLastProcessedResponse={setLastProcessedResponse}
            isInitialized={isInitialized}
            setIsInitialized={setIsInitialized}
            autoSelectScratch={autoSelectScratch}
            onScratchSelected={() => setAutoSelectScratch(false)}
            prefillRole={prefillRole}
            onPrefillConsumed={() => setPrefillRole(null)}
          />
        </div>
      </section>
    </div>
  );
};

export { PublishAssessmentUI };
